import type { EpisodeDisplayPreferences, SortConfig, ViewMode } from "@bastion/episodes";
import {
  DEFAULT_EPISODE_DISPLAY,
  DEFAULT_EPISODE_SIDEBAR_SECTIONS,
  EPISODE_SIDEBAR_SECTION_KEYS,
  EPISODE_CARD_FIELD_KEYS,
} from "@bastion/episodes";

const EPISODE_FIELD_KEY_SET = new Set<string>(EPISODE_CARD_FIELD_KEYS);

function isSortField(s: string): s is SortConfig["field"] {
  return s === "created_at" || s === "task_rating" || s === "frame_count";
}

function isSortOrder(s: string): s is SortConfig["order"] {
  return s === "asc" || s === "desc";
}

/** Parse `field:order` from the URL; returns undefined if malformed or unknown. */
export function parseSortSearchParam(raw: string): SortConfig | undefined {
  const colon = raw.indexOf(":");
  if (colon <= 0 || colon >= raw.length - 1) {
    return undefined;
  }
  const field = raw.slice(0, colon);
  const order = raw.slice(colon + 1);
  if (!isSortField(field) || !isSortOrder(order)) {
    return undefined;
  }
  return { field, order };
}

export function migrateLegacyEpisodeParams(params: URLSearchParams): void {
  const task = params.get("task");
  if (task && !params.get("search")) {
    params.set("search", task);
    params.delete("task");
  }
  const hw = params.getAll("hw");
  if (hw.length > 0 && params.getAll("hardware").length === 0) {
    for (const v of hw) {
      params.append("hardware", v);
    }
    params.delete("hw");
  }
  const from = params.get("from");
  const to = params.get("to");
  if ((from || to) && !params.get("dates_from") && !params.get("dates_to")) {
    if (from) {
      params.set("dates_from", from);
    }
    if (to) {
      params.set("dates_to", to);
    }
    params.delete("from");
    params.delete("to");
  }
  const legacySubtasks = params.getAll("subtasks");
  if (legacySubtasks.length > 0 && params.getAll("subtask_coverage").length === 0) {
    for (const v of legacySubtasks) {
      params.append("subtask_coverage", v);
    }
    const legacyOp = params.get("subtasks_op");
    if (legacyOp && !params.get("subtask_coverage_op")) {
      params.set("subtask_coverage_op", legacyOp);
    }
    params.delete("subtasks");
    params.delete("subtasks_op");
  }
}

export function decodeEpisodeDisplayFromParams(params: URLSearchParams): Partial<EpisodeDisplayPreferences> {
  const patch: Partial<EpisodeDisplayPreferences> = {};
  const sortRaw = params.get("sort");
  if (sortRaw) {
    const parsed = parseSortSearchParam(sortRaw);
    if (parsed) {
      patch.sort = parsed;
    }
  }
  const view = params.get("view");
  if (view === "grid" || view === "table") {
    patch.viewMode = view as ViewMode;
  }
  const pageSize = params.get("pageSize");
  if (pageSize) {
    const n = Number(pageSize);
    if (Number.isFinite(n) && n > 0) {
      patch.pageSize = n;
    }
  }
  const hidden = params.get("fields_hidden");
  if (hidden != null) {
    const keys = hidden
      .split(",")
      .map((k) => k.trim())
      .filter((k) => EPISODE_FIELD_KEY_SET.has(k));
    // Empty string here means "explicitly nothing hidden" — preserve as
    // empty array (vs. undefined which would fall back to default).
    patch.hiddenFields = keys;
  }
  const sidebar = params.get("sidebar");
  if (sidebar != null) {
    const requested = new Set(sidebar.split(","));
    patch.sidebarSections = EPISODE_SIDEBAR_SECTION_KEYS.filter((key) => requested.has(key));
  }
  return patch;
}

export function writeEpisodeDisplayToParams(
  params: URLSearchParams,
  display: EpisodeDisplayPreferences,
): void {
  const def = DEFAULT_EPISODE_DISPLAY;
  const sortStr = `${display.sort.field}:${display.sort.order}`;
  const defSort = `${def.sort.field}:${def.sort.order}`;
  if (sortStr !== defSort) {
    params.set("sort", sortStr);
  } else {
    params.delete("sort");
  }
  if (display.viewMode !== def.viewMode) {
    params.set("view", display.viewMode);
  } else {
    params.delete("view");
  }
  if (display.pageSize !== def.pageSize) {
    params.set("pageSize", String(display.pageSize));
  } else {
    params.delete("pageSize");
  }
  const hidden = display.hiddenFields ?? [];
  if (hidden.length > 0) {
    // Keep canonical order (matches EPISODE_CARD_FIELD_KEYS declaration)
    // so two equivalent selections produce the same URL.
    const ordered = EPISODE_CARD_FIELD_KEYS.filter((k) => hidden.includes(k));
    params.set("fields_hidden", ordered.join(","));
  } else {
    params.delete("fields_hidden");
  }
  const sections = display.sidebarSections ?? DEFAULT_EPISODE_SIDEBAR_SECTIONS;
  const orderedSections = EPISODE_SIDEBAR_SECTION_KEYS.filter((key) => sections.includes(key));
  const sidebar = orderedSections.join(",");
  // Keep the default explicit so a shared link overrides personal preferences.
  params.set("sidebar", sidebar);
}
